<i18n locale="en">
{
  "coming-soon": "Coming soon",
  "method-options": {
    "ai": "Make it with AI",
    "custom": "Make it yourself"
  },
  "exceeded-limit": {
    "title": "You've used your text blast.|You've used your {count} text blasts.",
    "body": "You can still create and schedule this blast for next month."
  },
  "location-select" : {
    "label": "Send a text to"
  }
}
</i18n>

<template>
  <div class="template-sidebar flex-col gap-8">
    <template v-if="showLocationSelector">
      <div class="flex-col gap-2">
        <OwnInputContainer :label="t('location-select.label')">
          <LocationSelector v-model="selectedLocation" />
        </OwnInputContainer>
      </div>
      <OwnRule type="dashed" />
    </template>

    <OwnTouchRadioGroup v-model="selectedMethod" :options="methodOptions" />
    <OwnOption class="template-sidebar__ai">
      <div class="flex-row align-center justify-between gap-4">
        <div class="flex-row align-center justify-start gap-4">
          <PhSparkle size="24" />

          <div class="flex-col gap-1">
            <OwnType
              :text="t('method-options.ai')"
              variant="title-regular"
              color="primary"
            />
            <OwnType
              :text="t('coming-soon')"
              variant="paragraph-small"
              color="secondary"
            />
          </div>
        </div>
      </div>
    </OwnOption>

    <OwnCallout
      v-if="smsBlastsSentThisMonth >= smsBlastsAllowedPerMonth"
      :title="t('exceeded-limit.title', { count: smsBlastsAllowedPerMonth })"
      :body="t('exceeded-limit.body')"
      type="warning"
      color="secondary"
    />
  </div>
</template>

<script lang="ts">
import { PhSparkle, PhPaintBrushHousehold } from '@phosphor-icons/vue'
import { markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import LocationSelector from '@/components/selectors/LocationSelector.vue'
import { useApplicationScope } from '@/core/store'
import { Location } from '@/core/types'
import { OlympusClient } from '@/OlympusClient'
import { OwnCallout } from '@/ui/OwnCallout'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnOption } from '@/ui/OwnOption'
import { OwnRule } from '@/ui/OwnRule'
import { OwnTouchRadioGroup } from '@/ui/OwnTouchRadioGroup'
import { OwnType } from '@/ui/OwnType'
import { mapModel } from '@/utils/computed'

export default {
  name: 'TemplateSidebar',
  components: {
    LocationSelector,
    OwnCallout,
    OwnInputContainer,
    OwnOption,
    OwnRule,
    OwnTouchRadioGroup,
    OwnType,
    PhSparkle,
  },
  props: {
    modelValue: { type: String, default: 'blank' },
    overrides: { type: Object, default: () => {} },
    previewLoaded: { type: Boolean, required: true },
  },
  emits: ['update:modelValue', 'update:overrides'],
  setup() {
    const { t } = useI18n()
    const { activeBrandId, activeLocationId } = useApplicationScope()

    return { activeBrandId, activeLocationId, t }
  },
  data() {
    return {
      methodOptions: [
        {
          icon: markRaw(PhPaintBrushHousehold),
          label: this?.t?.('method-options.custom') || 'Make it yourself',
          value: 'custom',
        },
      ],
      selectedLocation: undefined,
      selectedMethod: 'custom',
      smsBlastsAllowedPerMonth: 1,
      smsBlastsSentThisMonth: 0,
    }
  },
  computed: {
    ...mapModel('selectedOption'),
    ...mapGetters({
      locations: 'core/brand/active/locations',
    }),
    showLocationSelector() {
      const { locations } = this

      return locations?.length !== 1
    },
  },
  watch: {
    selectedLocation(newLocation) {
      this.$emit('update:overrides', { locationId: newLocation })
    },
  },
  async mounted() {
    await this.getCampaignLimits()

    const { locations, activeLocationId } = this

    if (
      activeLocationId &&
      locations?.some((loc: Location) => loc.id === activeLocationId)
    ) {
      this.selectedLocation = activeLocationId
    } else if (locations?.length === 1) {
      // Auto-select if brand has only one location
      this.selectedLocation = locations[0].id
    }
    // Otherwise selectedLocation remains null (All Locations)
  },
  methods: {
    async getCampaignLimits() {
      const { activeBrandId } = this

      const { smsBlastsSentThisMonth, smsBlastsAllowedPerMonth } =
        await OlympusClient.dashboard.v1.brands.brand.campaigns.checkOTCSmsLimits(
          {
            params: { brandId: activeBrandId },
            query: {
              date: new Date().toISOString(),
            },
          }
        )

      this.smsBlastsSentThisMonth = smsBlastsSentThisMonth
      this.smsBlastsAllowedPerMonth = smsBlastsAllowedPerMonth
    },
  },
}
</script>
<style lang="scss" scoped>
.template-sidebar {
  &__ai {
    margin-top: 12px;
    opacity: 0.5;
    box-shadow: none;
  }
}
</style>
